import React, { useState, useEffect } from 'react';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db, functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import Swal from 'sweetalert2';
import '../pages/DepartamentosDetalle.css';

/**
 * ModalEdicionSaldos
 * Componente modular para el ajuste seguro de saldos (Capital, Intereses, Saldo a Favor)
 * y regularización automática de mesesPendientes en Firestore mediante Cloud Function.
 */
export default function ModalEdicionSaldos({
    isOpen,
    casa,
    fraccionamientoId,
    userData,
    onClose,
    onSuccess
}) {
    const [capital, setCapital] = useState('0');
    const [intereses, setIntereses] = useState('0');
    const [saldoFavor, setSaldoFavor] = useState('0');

    const [mostrarIntereses, setMostrarIntereses] = useState(false);
    const [mostrarSaldoFavor, setMostrarSaldoFavor] = useState(false);

    const [isSaving, setIsSaving] = useState(false);
    const [saveStatusMessage, setSaveStatusMessage] = useState('');

    // Sincronizar estados locales al recibir o cambiar la vivienda
    useEffect(() => {
        if (casa) {
            const cap = Number(casa.adeudoCapital ?? 0);
            const inte = Number(casa.adeudoIntereses ?? casa.adeudoInteres ?? 0);
            const sf = Number(casa.saldoFavor ?? 0);

            setCapital(String(cap));
            setIntereses(String(inte));
            setSaldoFavor(String(sf));

            // Si ya cuenta con intereses registrados (>0), mantenerlo desplegado
            setMostrarIntereses(inte > 0);
            // Si ya cuenta con saldo a favor registrado (>0), mantenerlo desplegado
            setMostrarSaldoFavor(sf > 0);
        }
    }, [casa]);

    if (!isOpen || !casa) return null;

    const numCapital = parseFloat(capital) || 0;
    const numIntereses = parseFloat(intereses) || 0;
    const numSaldoFavor = parseFloat(saldoFavor) || 0;
    const totalAdeudoCalculado = numCapital + numIntereses;

    // Regla de exclusión mutua: si hay saldo a favor, los adeudos están bloqueados
    const isAdeudoDisabled = numSaldoFavor > 0;
    // Si hay cualquier adeudo, el saldo a favor está bloqueado
    const isSaldoFavorDisabled = totalAdeudoCalculado > 0;

    const handleCapitalChange = (valStr) => {
        setCapital(valStr);
        const val = parseFloat(valStr) || 0;
        if (val > 0) {
            setSaldoFavor('0');
        }
    };

    const handleInteresesChange = (valStr) => {
        setIntereses(valStr);
        const val = parseFloat(valStr) || 0;
        if (val > 0) {
            setSaldoFavor('0');
        }
    };

    const handleSaldoFavorChange = (valStr) => {
        setSaldoFavor(valStr);
        const val = parseFloat(valStr) || 0;
        if (val > 0) {
            setCapital('0');
            setIntereses('0');
        }
    };

    const handleToggleIntereses = () => {
        if (mostrarIntereses) {
            // Si se va a ocultar y tenía monto, advertir o limpiar a 0 para consistencia
            setIntereses('0');
            setMostrarIntereses(false);
        } else {
            setMostrarIntereses(true);
        }
    };

    const handleToggleSaldoFavor = () => {
        if (mostrarSaldoFavor) {
            setSaldoFavor('0');
            setMostrarSaldoFavor(false);
        } else {
            // Si tiene adeudos y activa saldo a favor, se limpiarán adeudos al ingresar monto
            setMostrarSaldoFavor(true);
        }
    };

    const handleSave = async () => {
        if (!casa?.id || !fraccionamientoId) return;

        // Validación de seguridad contable
        if (numSaldoFavor > 0 && (numCapital > 0 || numIntereses > 0)) {
            Swal.fire({
                title: 'Conflicto Financiero',
                text: 'No se permite guardar una cuenta con saldo a favor y adeudos al mismo tiempo.',
                icon: 'warning',
                confirmButtonColor: '#ef4444',
                confirmButtonText: 'Corregir'
            });
            return;
        }

        const finalCapital = numSaldoFavor > 0 ? 0 : numCapital;
        const finalIntereses = numSaldoFavor > 0 ? 0 : numIntereses;
        const finalAdeudo = numSaldoFavor > 0 ? 0 : (finalCapital + finalIntereses);
        const finalSaldoFavor = (finalCapital > 0 || finalIntereses > 0) ? 0 : numSaldoFavor;

        setIsSaving(true);
        setSaveStatusMessage('Guardando saldos en la vivienda...');

        try {
            // 1. Actualizar documento de la casa en Firestore
            const casaRef = doc(db, 'fraccionamientos', fraccionamientoId, 'casas', casa.id);
            await updateDoc(casaRef, {
                adeudo: finalAdeudo,
                adeudoCapital: finalCapital,
                adeudoIntereses: finalIntereses,
                adeudoInteres: finalIntereses,
                saldoFavor: finalSaldoFavor,
                ultimaModificacionSaldos: serverTimestamp(),
                modificadoPor: userData?.nombreCompleto || userData?.nombre || 'Administrador'
            });

            // 2. Registrar movimiento de auditoría institucional
            setSaveStatusMessage('Registrando auditoría del movimiento...');
            try {
                const { registerMovement } = await import('../utils/registerMovement');
                await registerMovement({
                    fraccionamientoId,
                    titulo: `Ajuste de saldos y regularización - Casa ${casa.numero}`,
                    descripcion: `El administrador ${userData?.nombreCompleto || userData?.nombre || 'Admin'} ajustó los saldos de la Casa ${casa.numero}. Capital: $${finalCapital.toLocaleString('es-MX', { minimumFractionDigits: 2 })}, Intereses: $${finalIntereses.toLocaleString('es-MX', { minimumFractionDigits: 2 })}, Saldo a Favor: $${finalSaldoFavor.toLocaleString('es-MX', { minimumFractionDigits: 2 })}. Regularización automática aplicada.`,
                    categoria: 'Finanzas',
                    tipoMovimiento: 'ajuste_saldo',
                    origen: 'administracion',
                    metadata: {
                        casaId: casa.id,
                        casaNumero: casa.numero,
                        adeudo: finalAdeudo,
                        adeudoCapital: finalCapital,
                        adeudoIntereses: finalIntereses,
                        saldoFavor: finalSaldoFavor,
                        adminUid: userData?.uid || null
                    }
                });
            } catch (errMove) {
                console.warn('Aviso en auditoría de saldos:', errMove);
            }

            // 3. Detonar Cloud Function de Regularización Automática
            setSaveStatusMessage('Actualizando estado de cuenta...');
            try {
                const migrarCasa = httpsCallable(functions, 'migrarCasaAMesesPendientes');
                const regResult = await migrarCasa({ fraccionamientoId, casaId: casa.id });
                console.log('Resultado de regularización automática:', regResult?.data);
            } catch (errReg) {
                console.warn('Aviso al ejecutar migrarCasaAMesesPendientes:', errReg);
            }

            setIsSaving(false);
            if (onSuccess) onSuccess();

            Swal.fire({
                title: '¡Saldos ajustados correctamente!',
                html: `<span style="color: #059669; font-weight: 500; font-size: 1rem; line-height: 1.5; display: inline-block;">Los saldos de la <strong>Casa ${casa.numero}</strong> han sido actualizados correctamente según el adeudo capital de mantenimiento.</span>`,
                icon: 'success',
                confirmButtonColor: '#1e3a8a',
                confirmButtonText: 'Aceptar'
            });
        } catch (error) {
            console.error('Error al guardar y regularizar saldos:', error);
            setIsSaving(false);
            Swal.fire({
                title: 'Error al Guardar',
                text: error.message || 'No se pudieron actualizar los saldos en la base de datos.',
                icon: 'error',
                confirmButtonColor: '#ef4444',
                confirmButtonText: 'Cerrar'
            });
        }
    };

    return (
        <div className="saldos-modal-backdrop" role="dialog" aria-modal="true">
            <div className="saldos-modal">
                <header className="saldos-modal-head">
                    <h3>💲 Edición de Saldos: Casa {casa.numero}</h3>
                    <button
                        type="button"
                        className="close-btn"
                        onClick={onClose}
                        disabled={isSaving}
                        title="Cerrar modal"
                    >
                        ×
                    </button>
                </header>

                <div className="saldos-modal-body">
                    <div className="saldos-modal-info">
                        <span><strong>Residente:</strong> {casa.propietario || casa.nombre_residente || 'Sin residente asignado'}</span>
                    </div>

                    <div className="saldos-warning-box warning-general">
                        ℹ️ <strong>Seguridad Financiera:</strong> Por integridad contable, un domicilio no puede tener saldo a favor y deudas al mismo tiempo. Al ingresar saldo a favor, las deudas se desactivarán automáticamente; al ingresar un adeudo, el saldo a favor se limpiará.
                    </div>

                    <div className="saldos-grid">
                        {/* Panel de Deudas / Adeudos */}
                        <div className={`saldos-panel panel-adeudos ${isAdeudoDisabled ? 'disabled' : ''}`}>
                            <div className="saldos-panel-header-row">
                                <h4>🛑 Deudas / Adeudos</h4>
                                <div className="saldos-calculated-badge" title="Suma automática de Capital + Intereses">
                                    Total: <strong>${totalAdeudoCalculado.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                                </div>
                            </div>

                            {/* Campo 1: Adeudo Capital (Directo y Visible) */}
                            <div className="saldos-field-group">
                                <label className="form-label">Adeudo Capital ($)</label>
                                <input
                                    type="number"
                                    step="any"
                                    min="0"
                                    className="form-input"
                                    value={capital}
                                    disabled={isAdeudoDisabled || isSaving}
                                    placeholder="0.00"
                                    onChange={(e) => handleCapitalChange(e.target.value)}
                                />
                                <span className="saldos-field-desc">Base neta del adeudo por cuotas ordinarias o mantenimiento.</span>
                            </div>

                            {/* Campo 2: Adeudo Intereses / Recargos (Colapsable / Opcional) */}
                            {!mostrarIntereses ? (
                                <button
                                    type="button"
                                    className="saldos-toggle-action-btn btn-toggle-intereses"
                                    onClick={handleToggleIntereses}
                                    disabled={isAdeudoDisabled || isSaving}
                                >
                                    ➕ Ajustar Intereses o Recargos
                                </button>
                            ) : (
                                <div className="saldos-field-group saldos-field-collapsible">
                                    <div className="saldos-label-with-toggle">
                                        <label className="form-label">Adeudo Intereses / Recargos ($)</label>
                                        <button
                                            type="button"
                                            className="saldos-btn-collapse-link"
                                            onClick={handleToggleIntereses}
                                            disabled={isSaving}
                                            title="Ocultar y restablecer campo de intereses a 0"
                                        >
                                            ▲ Ocultar campo
                                        </button>
                                    </div>
                                    <input
                                        type="number"
                                        step="any"
                                        min="0"
                                        className="form-input"
                                        value={intereses}
                                        disabled={isAdeudoDisabled || isSaving}
                                        placeholder="0.00"
                                        onChange={(e) => handleInteresesChange(e.target.value)}
                                        autoFocus
                                    />
                                    <span className="saldos-field-desc">Monto por intereses de mora, penalidades o sanciones.</span>
                                </div>
                            )}
                        </div>

                        {/* Panel de Saldo a Favor */}
                        <div className={`saldos-panel panel-saldo-favor ${isSaldoFavorDisabled ? 'disabled' : ''}`}>
                            <div className="saldos-panel-header-row">
                                <h4>🟢 Saldo a Favor</h4>
                                <div className="saldos-calculated-badge badge-favor" title="Crédito a favor para futuros cargos">
                                    Crédito: <strong>${numSaldoFavor.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                                </div>
                            </div>

                            {/* Campo Saldo a Favor (Colapsable / Opcional) */}
                            {!mostrarSaldoFavor ? (
                                <button
                                    type="button"
                                    className="saldos-toggle-action-btn btn-toggle-favor"
                                    onClick={handleToggleSaldoFavor}
                                    disabled={isSaldoFavorDisabled || isSaving}
                                >
                                    ➕ Registrar / Ajustar Saldo a Favor
                                </button>
                            ) : (
                                <div className="saldos-field-group saldos-field-collapsible">
                                    <div className="saldos-label-with-toggle">
                                        <label className="form-label">Saldo a Favor ($)</label>
                                        <button
                                            type="button"
                                            className="saldos-btn-collapse-link"
                                            onClick={handleToggleSaldoFavor}
                                            disabled={isSaving}
                                            title="Ocultar y restablecer saldo a favor a 0"
                                        >
                                            ▲ Ocultar campo
                                        </button>
                                    </div>
                                    <input
                                        type="number"
                                        step="any"
                                        min="0"
                                        className="form-input saldo-favor-input"
                                        value={saldoFavor}
                                        disabled={isSaldoFavorDisabled || isSaving}
                                        placeholder="0.00"
                                        onChange={(e) => handleSaldoFavorChange(e.target.value)}
                                        autoFocus
                                    />
                                    <span className="saldos-field-desc">Crédito disponible para futuros abonos o cargos de la vivienda.</span>
                                </div>
                            )}
                        </div>
                    </div>

                    {isAdeudoDisabled && (
                        <div className="saldos-status-alert alert-green">
                            🔒 <strong>Campos de Adeudo Bloqueados:</strong> Se desactivaron porque el domicilio cuenta con saldo a favor registrado.
                        </div>
                    )}

                    {isSaldoFavorDisabled && (
                        <div className="saldos-status-alert alert-red">
                            🔒 <strong>Saldo a Favor Bloqueado:</strong> Se desactivó porque el domicilio cuenta con adeudos activos registrados.
                        </div>
                    )}

                    {isSaving && (
                        <div className="saldos-saving-progress-box">
                            <span className="saldos-spinner"></span>
                            <span>{saveStatusMessage || 'Procesando cambios...'}</span>
                        </div>
                    )}
                </div>

                <footer className="saldos-modal-foot">
                    <button
                        type="button"
                        className="btn-secondary"
                        onClick={onClose}
                        disabled={isSaving}
                    >
                        Cancelar
                    </button>
                    <button
                        type="button"
                        className="btn-primary"
                        onClick={handleSave}
                        disabled={isSaving}
                    >
                        {isSaving ? '⏳ Guardando cambios...' : '💾 Guardar Cambios'}
                    </button>
                </footer>
            </div>
        </div>
    );
}
